import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { useApp } from '../AppContext';
import Header from '../components/Header';

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { handlePlaidSuccess, currentUser } = useApp();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch link token from our Plaid API
  const createLinkToken = useCallback(async () => {
    if (!currentUser) {
      console.error('No user logged in');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://api.neurofininc.com/api/create_link_token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: currentUser.uid,
          client_name: 'Money Mindset Tool',
          redirect_uri: 'https://tools.minimoney.app/plaid/redirect',
          platform: 'web',
        }),
      });

      const data = await response.json();
      setLinkToken(data.link_token);
    } catch (error) {
      console.error('Error creating link token:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Plaid Link configuration
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken) => {
      try {
        await handlePlaidSuccess(publicToken);
        navigate('/mmt/overview');
      } catch (error) {
        console.error('Error handling Plaid success:', error);
      }
    },
    onExit: (err) => {
      if (err) {
        console.error('Plaid Link exit with error:', err);
      }
    },
  });

  const handleConnect = async () => {
    if (!linkToken) {
      await createLinkToken();
      // The useEffect below will open Plaid when token is ready
    } else if (ready) {
      open();
    }
  };

  // Open Plaid when link token is ready
  React.useEffect(() => {
    if (linkToken && ready) {
      open();
    }
  }, [linkToken, ready, open]);

  return (
    <div className="min-h-screen bg-primary">
      <Header />
      <div className="flex flex-col items-center justify-center p-6 min-h-[calc(100vh-80px)]">
        <div className="max-w-3xl text-center">
        <h1 className="text-6xl font-bold text-white mb-8">
          Money Mindset Tool
        </h1>

        <p className="text-xl text-white/90 leading-relaxed mb-12">
          Welcome to the money mindset tool. This tool combines how you spend
          with how you feel to give you a full picture on your money mindset. We
          use behavioral economics, psychology, and your financial transactions
          to give you deeper insights on how and why you spend the way you do!
        </p>

        <button
          onClick={handleConnect}
          disabled={loading}
          className={`bg-white text-primary px-16 py-6 rounded-full text-2xl font-semibold transition-all transform shadow-xl ${
            loading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-100 hover:scale-105'
          }`}
        >
          {loading ? 'Loading...' : 'Connect Your Accounts'}
        </button>
      </div>
      </div>
    </div>
  );
};

export default Landing;
